/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BusinessType } from '../models/business-type';
import { GenericResponse } from '../models/generic-response';
import { ZatcaRetryRequest } from '../models/zatca-retry-request';

/**
 * A3 pdf
 */
@Injectable({ providedIn: 'root' })
export class ZatcaService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllZatcaInvoicePdf()` */
  static readonly GetAllZatcaInvoicePdfPath = '/api/v1/zatca-invoice';

  /**
   * To download multiple invoices in a zip folder.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllZatcaInvoicePdf()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZatcaInvoicePdf$Response(
    params?: {
      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering for reports by  business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filtering for reports by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filtering for reports by date type
       */
      dateType?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.GetAllZatcaInvoicePdfPath,
      'get'
    );
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('clientId', params.clientId, {});
      rb.query('businessType', params.businessType, {});
      rb.query('invoiceType', params.invoiceType, {});
      rb.query('dateType', params.dateType, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * To download multiple invoices in a zip folder.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllZatcaInvoicePdf$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZatcaInvoicePdf(
    params?: {
      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * This allows filtering for reports by  business type
       */
      businessType?: Array<BusinessType>;

      /**
       * This adds a filtering for reports by invoice type
       */
      invoiceType?: string;

      /**
       * This adds a filtering for reports by date type
       */
      dateType?: string;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.getAllZatcaInvoicePdf$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `getZatcaInvoiceById()` */
  static readonly GetZatcaInvoiceByIdPath = '/api/v1/zatca-invoice/{id}';

  /**
   * To download zatca invoice pdfA3 using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getZatcaInvoiceById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getZatcaInvoiceById$Response(
    params: {
      /**
       * The zatca invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.GetZatcaInvoiceByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'blob', accept: 'application/pdf', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * To download zatca invoice pdfA3 using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getZatcaInvoiceById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getZatcaInvoiceById(
    params: {
      /**
       * The zatca invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.getZatcaInvoiceById$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getZatcaInvoiceXmlById()` */
  static readonly GetZatcaInvoiceXmlByIdPath =
    '/api/v1/zatca-invoice/{id}/download-xml';

  /**
   * To download zatca invoice xml using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getZatcaInvoiceXmlById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getZatcaInvoiceXmlById$Response(
    params: {
      /**
       * The zatca invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.GetZatcaInvoiceXmlByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'blob', accept: 'application/xml', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * To download zatca invoice xml using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getZatcaInvoiceXmlById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getZatcaInvoiceXmlById(
    params: {
      /**
       * The zatca invoice's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.getZatcaInvoiceXmlById$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `retryZatcaInvoiceUploadById()` */
  static readonly RetryZatcaInvoiceUploadByIdPath =
    '/api/v1/zatca-invoice-retry/{id}';

  /**
   * Retry Zatca Invoice Upload.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `retryZatcaInvoiceUploadById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  retryZatcaInvoiceUploadById$Response(
    params: {
      /**
       * The ID of the invoice to be retried.
       */
      id: number;
      body: ZatcaRetryRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.RetryZatcaInvoiceUploadByIdPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Retry Zatca Invoice Upload.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `retryZatcaInvoiceUploadById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  retryZatcaInvoiceUploadById(
    params: {
      /**
       * The ID of the invoice to be retried.
       */
      id: number;
      body: ZatcaRetryRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.retryZatcaInvoiceUploadById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `resubmitZatcaInvoiceById()` */
  static readonly ResubmitZatcaInvoiceByIdPath =
    '/api/v1/zatca-invoice-resubmit/{id}';

  /**
   * Resubmit Zatca Invoice Upload.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resubmitZatcaInvoiceById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resubmitZatcaInvoiceById$Response(
    params: {
      /**
       * The ID of the invoice to be Resubmit.
       */
      id: number;
      body: ZatcaRetryRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.ResubmitZatcaInvoiceByIdPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Resubmit Zatca Invoice Upload.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resubmitZatcaInvoiceById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resubmitZatcaInvoiceById(
    params: {
      /**
       * The ID of the invoice to be Resubmit.
       */
      id: number;
      body: ZatcaRetryRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.resubmitZatcaInvoiceById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `retryOracleEbsInvoiceUploadById()` */
  static readonly RetryOracleEbsInvoiceUploadByIdPath =
    '/api/v1/oracle-ebs-invoice-retry/{id}';

  /**
   * Retry Oracle EBS Invoice Upload.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `retryOracleEbsInvoiceUploadById()` instead.
   *
   * This method doesn't expect any request body.
   */
  retryOracleEbsInvoiceUploadById$Response(
    params: {
      /**
       * The ID of the invoice to be retried.
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ZatcaService.RetryOracleEbsInvoiceUploadByIdPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Retry Oracle EBS Invoice Upload.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `retryOracleEbsInvoiceUploadById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  retryOracleEbsInvoiceUploadById(
    params: {
      /**
       * The ID of the invoice to be retried.
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.retryOracleEbsInvoiceUploadById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }
}
